/**
 * PB-FOUNDATION navigation kernel — step map, skip-before-paint, queue flush budget.
 * PB-FND-1: extracted behind BUILDER_DATA.navigationKernel (default on since PB-FND-19).
 * PB-CART-1: child-line cart_edit uses reduced step map (variant + personalize only).
 */

import { MAIN_BODY_STEP_KIND_ORDER, STEP_KINDS } from '@theme/pb-step-kinds';
import { countVisiblePersonalizationFields } from '@theme/pb-derived-properties-logic';
import {
  getChildLineStepMap,
  isChildLineCartEdit,
  isCartEditMainPresentation,
  resolveNavContextFromBuilderState,
} from '@theme/pb-cart-child-edit';

export { MAIN_BODY_STEP_KIND_ORDER, STEP_KINDS } from '@theme/pb-step-kinds';

export function isNavigationKernelEnabled(data) {
  const source = data || (typeof window !== 'undefined' ? window.BUILDER_DATA : null);
  return !!(source && source.navigationKernel === true);
}

export function countVariantSteps(data) {
  if (data?.giftClaim === true && data.recipientChoosesVariant !== true) return 0;
  const options = data?.optionsWithValues || data?.options || [];
  if (!Array.isArray(options)) return 0;
  return options.filter(function (opt) {
    const name = String(opt?.name || '').toLowerCase();
    const values = opt?.values;
    const size = Array.isArray(values) ? values.length : 0;
    return name !== 'title' && size > 0;
  }).length;
}

export function isShopifyBundleModeFromData(data) {
  return !!(data && data.shopifyBundle === true);
}

function getDynamicPersonalizationStepCount(data, deps) {
  if (typeof deps?.getDynamicPersonalizationStepCount === 'function') {
    return deps.getDynamicPersonalizationStepCount();
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const current = window.BUILDER_DATA;
  if (data !== current && (!data?.mainProduct || data.mainProduct !== current?.mainProduct)) return 0;
  const root = document.getElementById('main-flow-container');
  return root?.querySelectorAll('.pb-bottom-wrapper .pb-step-viewport .pb-step[data-topic-parent]').length || 0;
}

export function countPersonalizationSteps(data, deps) {
  const fields = isShopifyBundleModeFromData(data)
    ? (
      data.shopifyBundleMergedPersonalization ||
      data.mainProduct?.config ||
      data.mainProduct?.personalization ||
      []
    )
    : data?.mainProduct?.config || data?.mainProduct?.personalization || [];
  const dynamicCount = getDynamicPersonalizationStepCount(data, deps);
  return countVisiblePersonalizationFields(fields) +
    (Number.isInteger(dynamicCount) && dynamicCount > 0 ? dynamicCount : 0);
}

export function hasAnyAddonsFromData(data) {
  const mainAddons = data?.mainProduct?.addons || [];
  if (Array.isArray(mainAddons) && mainAddons.length > 0) return true;
  const bundles = Array.isArray(data?.bundles) ? data.bundles : [];
  for (let i = 0; i < bundles.length; i += 1) {
    const items = Array.isArray(bundles[i]?.items) ? bundles[i].items : [];
    for (let j = 0; j < items.length; j += 1) {
      const addons = items[j]?.addons;
      if (Array.isArray(addons) && addons.length > 0) return true;
    }
  }
  return false;
}

export function hasEffectiveOfferBundlesFromData(data) {
  if (isShopifyBundleModeFromData(data)) return false;
  const bundles = data?.bundles;
  return !!(bundles && bundles.length > 0);
}

export function resolveStepMap(data, deps, navContext) {
  const builderState = typeof window !== 'undefined' ? window.builderState : null;
  if (isCartEditMainPresentation(builderState)) {
    return buildPersonalizationOnlyStepMap(data, deps);
  }

  const childCtx =
    navContext && isChildLineCartEdit(navContext)
      ? navContext
      : typeof deps?.getNavContext === 'function'
        ? deps.getNavContext()
        : resolveNavContextFromBuilderState(
            typeof window !== 'undefined' ? window.builderState : null,
            typeof window !== 'undefined' ? window.BUILDER_DATA : null,
            typeof window !== 'undefined' ? window.builderState?._pbCartEditCart : null
          );
  if (childCtx && isChildLineCartEdit(childCtx)) {
    return getChildLineStepMap(childCtx, childCtx.itemData);
  }
  return buildMainStepMap(data, deps);
}

/** Personalize + variant body blocks only — cart edit (main line + child line), never offer/addon. */
export function buildPersonalizationOnlyStepMap(data, deps) {
  const blockCounts = {
    [STEP_KINDS.PERSONALIZE]: countPersonalizationSteps(data, deps),
    [STEP_KINDS.VARIANT]: countVariantSteps(data),
  };
  const steps = [];
  let index = 1;

  for (const kind of MAIN_BODY_STEP_KIND_ORDER) {
    const count = blockCounts[kind] || 0;
    for (let i = 0; i < count; i += 1) {
      steps.push({ index: index++, kind });
    }
  }

  return steps;
}

export function buildMainStepMap(data, deps) {
  const steps = buildPersonalizationOnlyStepMap(data, deps);
  if (data?.giftClaim === true) return steps;
  let index = steps.length ? steps[steps.length - 1].index + 1 : 1;

  const hasOfferShell = data?.hasOfferBundles === true && !isShopifyBundleModeFromData(data);
  if (hasOfferShell) {
    steps.push({ index: index++, kind: STEP_KINDS.OFFER });
  }

  const hasAddonShell =
    typeof deps?.hasAddonShell === 'function'
      ? deps.hasAddonShell(data)
      : data?.hasAddonStep === true || hasAnyAddonsFromData(data);
  if (hasAddonShell) {
    steps.push({ index: index++, kind: STEP_KINDS.ADDON });
  }

  return steps;
}

/** First personalize step index under MAIN_BODY_STEP_KIND_ORDER (PB-STEP-ORDER). */
export function findFirstPersonalizationStepIndex(data) {
  const map = buildPersonalizationOnlyStepMap(data);
  const entry = map.find((step) => step.kind === STEP_KINDS.PERSONALIZE);
  return entry?.index ?? null;
}

/** First variant step index after personalization blocks (sold-out repick / draft restore). */
export function findFirstVariantStepIndex(data) {
  const map = buildPersonalizationOnlyStepMap(data);
  const entry = map.find((step) => step.kind === STEP_KINDS.VARIANT);
  return entry?.index ?? null;
}

export function getMaxMainSteps(data, deps, navContext) {
  const map = resolveStepMap(data, deps, navContext);
  if (!map.length) return 1;
  return map[map.length - 1].index;
}

export function getStepKind(stepIndex, data, deps, navContext) {
  const map = resolveStepMap(data, deps, navContext);
  const entry = map.find(function (step) {
    return step.index === stepIndex;
  });
  return entry ? entry.kind : null;
}

export function shouldSkipStepBeforePaint(stepIndex, data, deps, navContext) {
  const kind = getStepKind(stepIndex, data, deps, navContext);
  if (kind === STEP_KINDS.OFFER) {
    const hasBundles =
      typeof deps?.hasEffectiveOfferBundles === 'function'
        ? deps.hasEffectiveOfferBundles()
        : hasEffectiveOfferBundlesFromData(data);
    return !hasBundles;
  }
  if (kind === STEP_KINDS.ADDON) {
    const hasAddons =
      typeof deps?.hasAnyAddons === 'function'
        ? deps.hasAnyAddons(data)
        : hasAnyAddonsFromData(data);
    return !hasAddons;
  }
  return false;
}

export function computeNavigationBudget(maxSteps) {
  return (maxSteps || 12) + 4;
}

export function resolveStepAfterDelta(currentStep, delta, data, deps, navContext) {
  const direction = delta === -1 ? -1 : 1;
  const maxSteps = getMaxMainSteps(data, deps, navContext);
  if (direction > 0 && currentStep >= maxSteps) {
    return currentStep + 1;
  }
  let target = currentStep + direction;
  if (target < 1) return 1;
  if (target > maxSteps) return direction > 0 ? maxSteps + 1 : maxSteps;

  let budget = computeNavigationBudget(maxSteps);
  while (budget-- > 0 && shouldSkipStepBeforePaint(target, data, deps, navContext)) {
    target += direction;
    if (target < 1) return 1;
    if (target > maxSteps) {
      return direction > 0 ? maxSteps + 1 : maxSteps;
    }
  }
  return target;
}

export function createStepNavigationQueue(deps) {
  let pending = null;
  let flushScheduled = false;
  let flushing = false;

  function flush() {
    if (flushing) return;
    flushing = true;
    try {
      const ctx = typeof deps.getActiveContext === 'function' ? deps.getActiveContext() : null;
      let budget = computeNavigationBudget(ctx?.maxSteps);
      while (pending && budget-- > 0) {
        const next = pending;
        pending = null;
        let moved = false;

        if (next.kind === 'offer' || next.kind === 'any') {
          if (typeof deps.maybeSkipEmptyOfferStep === 'function') {
            moved = deps.maybeSkipEmptyOfferStep(next.direction) || moved;
          }
        }
        if (!moved && (next.kind === 'addon' || next.kind === 'any')) {
          if (typeof deps.maybeSkipEmptyAddonStep === 'function') {
            moved = deps.maybeSkipEmptyAddonStep(next.direction) || moved;
          }
        }
        if (!moved && (next.kind === 'dl' || next.kind === 'any')) {
          if (typeof deps.maybeSkipDesignLibraryStep === 'function') {
            moved = deps.maybeSkipDesignLibraryStep(next.direction) || moved;
          }
        }
        if (moved) {
          pending = { kind: 'any', direction: next.direction };
        }
      }
    } finally {
      flushing = false;
    }
  }

  function scheduleFlush() {
    if (flushScheduled) return;
    flushScheduled = true;
    queueMicrotask(function () {
      flushScheduled = false;
      flush();
    });
  }

  return {
    queueStepNavigation(kind, direction) {
      pending = { kind: kind || 'any', direction: direction === -1 ? -1 : 1 };
      scheduleFlush();
    },
    _scheduleStepNavigationFlush: scheduleFlush,
    _flushPendingStepNavigation: flush,
  };
}

export function createNavigationKernel(deps) {
  const queue = createStepNavigationQueue(deps);

  function getData() {
    return typeof deps.getBuilderData === 'function' ? deps.getBuilderData() : null;
  }

  function getNavContext() {
    return typeof deps.getNavContext === 'function' ? deps.getNavContext() : null;
  }

  function blockOffersAddonsDoorway(navOpts) {
    if (typeof window !== 'undefined' && window.builderState?.mode === 'SUB') return false;
    if (typeof window === 'undefined' || !window.PBAiSessionGate) return false;
    const gate = window.PBAiSessionGate;
    if (typeof gate.shouldBlockOffersAddonsNavigation !== 'function') return false;
    const blocked = gate.shouldBlockOffersAddonsNavigation({
      builderState: window.builderState,
      data: getData(),
      deps,
      navContext: getNavContext(),
      ...navOpts,
    });
    if (!blocked) return false;
    if (typeof gate.showOffersDoorwayBlockHint === 'function') gate.showOffersDoorwayBlockHint();
    if (window.PBFooterNav && typeof window.PBFooterNav.scheduleSync === 'function') {
      window.PBFooterNav.scheduleSync();
    } else if (window.productBuilder && typeof window.productBuilder.checkStepValidity === 'function') {
      window.productBuilder.checkStepValidity();
    }
    return true;
  }

  function flowLockBlocksNavigation(navOpts) {
    if (typeof window === 'undefined' || !window.PBFlowLock) return false;
    if (typeof window.PBFlowLock.canNavigate !== 'function') return false;
    return !window.PBFlowLock.canNavigate(navOpts);
  }

  function showFlowLockBlockHint(navOpts) {
    if (window.PBFooterNav && typeof window.PBFooterNav.showBlockHint === 'function') {
      const reason =
        typeof window.PBFlowLock.getBlockReason === 'function'
          ? window.PBFlowLock.getBlockReason(navOpts)
          : null;
      window.PBFooterNav.showBlockHint(
        (reason && reason.message) || 'Stop or cancel your recording first.'
      );
    }
    if (window.productBuilder && typeof window.productBuilder.checkStepValidity === 'function') {
      window.productBuilder.checkStepValidity();
    }
  }

  function buildFlowLockNavOpts(ctx, options) {
    const builderState = typeof window !== 'undefined' ? window.builderState : null;
    const baseOpts = {
      currentStep: ctx?.step,
      delta: options?.delta,
      targetStep: options?.targetStep,
      data: getData(),
      deps,
      navContext: getNavContext(),
    };
    const classify =
      typeof window !== 'undefined' && window.PBAiSessionGate?.classifyNavigationIntent;
    const navIntent = typeof classify === 'function' ? classify(builderState, baseOpts) : undefined;
    return {
      currentStep: ctx?.step,
      delta: options?.delta,
      targetStep: options?.targetStep,
      navIntent,
    };
  }

  return {
    getStepKind(stepIndex) {
      return getStepKind(stepIndex, getData(), deps, getNavContext());
    },
    buildStepMap() {
      return resolveStepMap(getData(), deps, getNavContext());
    },
    shouldSkipStepBeforePaint(stepIndex) {
      return shouldSkipStepBeforePaint(stepIndex, getData(), deps, getNavContext());
    },
    navigate(opts) {
      const options = opts || {};
      const ctx = typeof deps.getActiveContext === 'function' ? deps.getActiveContext() : null;
      if (!ctx) return;
      const navContext = getNavContext();
      const flowNavOpts = buildFlowLockNavOpts(ctx, options);

        if (
          options.delta != null &&
          options.delta !== 0 &&
          flowLockBlocksNavigation(flowNavOpts)
        ) {
          showFlowLockBlockHint(flowNavOpts);
          return;
        }

      if (options.targetStep != null) {
        const currentStep = ctx.step;
        const target = parseInt(options.targetStep, 10);
        if (!isNaN(target) && target !== currentStep) {
          const targetFlowOpts = buildFlowLockNavOpts(ctx, { targetStep: target });
          if (flowLockBlocksNavigation(targetFlowOpts)) {
            showFlowLockBlockHint(targetFlowOpts);
            return;
          }
        }
      }

      if (options.delta != null) {
        const delta = options.delta === -1 ? -1 : 1;
        if (
          delta === 1 &&
          ctx.maxSteps &&
          ctx.step >= ctx.maxSteps &&
          typeof deps.moveStepLegacy === 'function'
        ) {
          if (blockOffersAddonsDoorway({ currentStep: ctx.step, delta: 1 })) {
            return;
          }
          deps.moveStepLegacy(1);
          return;
        }
        const resolved = resolveStepAfterDelta(ctx.step, delta, getData(), deps, navContext);
        const actualDelta = resolved - ctx.step;
        if (
          actualDelta > 0 &&
          blockOffersAddonsDoorway({ currentStep: ctx.step, targetStep: resolved })
        ) {
          return;
        }
        if (actualDelta !== 0 && typeof deps.moveStepLegacy === 'function') {
          deps.moveStepLegacy(actualDelta);
        }
        return;
      }

      if (options.targetStep != null && typeof deps.jumpToStepLegacy === 'function') {
        let target = parseInt(options.targetStep, 10);
        if (isNaN(target)) return;
        const maxSteps =
          typeof window !== 'undefined' && window.builderState && window.builderState.mode === 'SUB'
            ? ctx.maxSteps || 1
            : getMaxMainSteps(getData(), deps, navContext);
        target = Math.max(1, Math.min(target, maxSteps));
        if (shouldSkipStepBeforePaint(target, getData(), deps, navContext)) {
          const direction = target > ctx.step ? 1 : -1;
          target = resolveStepAfterDelta(ctx.step, direction, getData(), deps, navContext);
        }
        if (target > ctx.step && blockOffersAddonsDoorway({ currentStep: ctx.step, targetStep: target })) {
          return;
        }
        deps.jumpToStepLegacy(target);
      }
    },
    queueStepNavigation: queue.queueStepNavigation.bind(queue),
    _scheduleStepNavigationFlush: queue._scheduleStepNavigationFlush,
    _flushPendingStepNavigation: queue._flushPendingStepNavigation,
  };
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBNavigation = {
    STEP_KINDS,
    isNavigationKernelEnabled,
    countVariantSteps,
    countPersonalizationSteps,
    hasAnyAddonsFromData,
    hasEffectiveOfferBundlesFromData,
    isShopifyBundleModeFromData,
    buildMainStepMap,
    findFirstPersonalizationStepIndex,
    findFirstVariantStepIndex,
    resolveStepMap,
    getMaxMainSteps,
    getStepKind,
    shouldSkipStepBeforePaint,
    computeNavigationBudget,
    resolveStepAfterDelta,
    createStepNavigationQueue,
    createNavigationKernel,
  };
}

attachToWindow();
